/*
Copyright 2026 Symbiotek

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { secureRandomString } from "matrix-js-sdk/lib/randomstring";

import { type Session } from "../ClientContext";
import { Config } from "../config/Config";
import { generateRandomName } from "./generateRandomName";
import { provisionGuestUser } from "./provisionGuestUser";
import { useInteractiveRegistration } from "./useInteractiveRegistration";
import { useRecaptcha } from "./useRecaptcha";

interface UseRegisterGuestUserType {
  privacyPolicyUrl?: string;
  recaptchaId: string;
  /**
   * Creates a throwaway account and returns a client logged into it. The caller
   * decides when to adopt it as the active session.
   */
  registerGuestUser: (displayName: string) => Promise<[MatrixClient, Session]>;
  /** Resets the captcha after a failed attempt. */
  reset: () => void;
}

/**
 * Provides the "join without an account" registration used by both the landing
 * page and room links.
 *
 * Guests are normally registered straight from the browser. Homeservers that
 * delegate authentication to Matrix Authentication Service don't allow that, so
 * when `guest_provisioning_url` is configured the account is created by that
 * service instead. See {@link provisionGuestUser}.
 *
 * @param oldClient If there is an already authenticated client it should be
 * passed here, so that it can be signed out first.
 */
export function useRegisterGuestUser(
  oldClient?: MatrixClient,
): UseRegisterGuestUserType {
  const { privacyPolicyUrl, recaptchaKey, register } =
    useInteractiveRegistration(oldClient);
  const { execute, reset, recaptchaId } = useRecaptcha(recaptchaKey);

  const registerGuestUser = useCallback(
    async (displayName: string): Promise<[MatrixClient, Session]> => {
      const provisioningUrl = Config.get().guest_provisioning_url;
      if (provisioningUrl) {
        return provisionGuestUser(
          provisioningUrl,
          Config.defaultHomeserverUrl()!,
          displayName,
          oldClient,
        );
      }

      const recaptchaResponse = await execute();
      return register(
        generateRandomName(),
        secureRandomString(16),
        displayName,
        recaptchaResponse,
        true,
      );
    },
    [execute, oldClient, register],
  );

  return { privacyPolicyUrl, recaptchaId, registerGuestUser, reset };
}
